import type { AtlasKeyedCommand } from "@/components/ecv2/components/whiteboardEC/runtime/keymap";
import type { AtlasCommandResult } from "@/components/ecv2/components/whiteboardEC/runtime/pluginHost";
import type { ControlSurface } from "@/components/ecv2/engine/surfaceFramework/registry/controlSurfaceRegistry";
import { z } from "zod";

export const ATLAS_ACTIONS_TARGET = "atlas";
export const ATLAS_EMPTY_COMMAND_PARAMS = z.object({}).strict();

export type AtlasSurfaceRead = Readonly<{
  description: string;
  getValue: () => unknown;
}>;

export function atlasPluginSurface(
  plugin: Readonly<{ key: string; label: string }>,
  commands: Readonly<Record<string, AtlasKeyedCommand>>,
  reads: Readonly<Record<string, AtlasSurfaceRead>>,
  run: (id: string, command: AtlasKeyedCommand, params: unknown) => AtlasCommandResult
): ControlSurface {
  const entries: ControlSurface = {};
  const meta = {
    surfaceId: `atlas.${plugin.key}`,
    surfaceName: plugin.label,
    surfaceSummary: `Atlas ${plugin.label.toLowerCase()}.`,
    surfaceDescription: `${plugin.label} commands and current state. Commands use the same validation and permission gates as the UI. State is read-only; use actions to change it.`,
  };
  for (const [name, command] of Object.entries(commands)) {
    const id = `${plugin.key}.${name}`;
    entries[id] = {
      ...meta,
      kind: "action",
      descriptor: {
        id,
        name: command.label,
        description: command.description ?? command.label,
        paramsSchema: command.params ?? ATLAS_EMPTY_COMMAND_PARAMS,
      },
      call: async (params) => {
        const result = run(id, command, params);
        return result.ok ? await result.result : result;
      },
    };
  }
  for (const [name, read] of Object.entries(reads)) {
    const id = `${plugin.key}.${name}`;
    if (entries[id]) {
      throw new Error(`Atlas surface key ${id} is already a command.`);
    }
    entries[id] = {
      ...meta,
      kind: "state",
      descriptor: {
        id,
        name,
        description: read.description,
        defaultValue: undefined,
        persistence: "none",
        schema: z.unknown(),
      },
      getValue: read.getValue,
      setValueValidated: () => {
        throw new Error(`${id} is read-only; use commands.`);
      },
    };
  }
  return entries;
}
